import dynamicLicenses from '@/assets/json/dynamicLicenses.json';

type SealType = 'ra-reputation' | 'ra-verified-seal';

export const useLicenses = () => {
  const { visitorOrUserCountry } = useParameters();

  const findByCountry = computed(() => {
    if (!visitorOrUserCountry.value) {
      return dynamicLicenses?.countries[0];
    }

    const findItem = dynamicLicenses?.countries.find((item) =>
      item.country.includes(visitorOrUserCountry.value),
    );

    return findItem || dynamicLicenses?.countries[0];
  });

  const licenseText = computed(() => {
    if (!findByCountry.value) {
      return;
    }

    const { companyFullName, companyCnpj, companyAddress } = useCompanyData();

    return findByCountry.value?.textLicense
      .replaceAll('{{ appUrl }}', appUrlUpper())
      .replaceAll('{{ companyName }}', companyFullName.value)
      .replaceAll('{{ companyCnpj }}', companyCnpj.value)
      .replaceAll('{{ companyAddress }}', companyAddress.value);
  });

  const primarySeal = computed<any>(() => {
    if (!findByCountry.value) {
      return;
    }

    return findByCountry.value?.primarySeal;
  });

  const primarySealId = computed<any>(() => {
    if (!primarySeal.value) {
      return;
    }

    return primarySeal.value?.id;
  });

  const primarySealIsAnjseal = computed(() => {
    return primarySeal.value?.type === 'anjseal' && primarySealId.value;
  });

  const primarySealIsAntillephone = computed(() => {
    return primarySeal.value?.type === 'antillephone' && primarySealId.value;
  });

  const primarySealIsTypeCuracao = computed(() => {
    return primarySeal.value?.type === 'curacao' && primarySeal.value?.src;
  });

  const primarySealIsTypeBase = computed(() => {
    return primarySealIsAntillephone.value || primarySealIsAnjseal.value;
  });

  const primarySealIsTypeImage = computed(() => {
    return primarySeal.value?.type === 'image' && primarySeal.value?.src;
  });

  const customSeals = computed<any>(() => {
    if (!findByCountry.value) {
      return;
    }

    return findByCountry.value?.customSeals;
  });

  const customSealsOnPatronages = computed<any>(() => {
    if (!customSeals.value) {
      return;
    }

    const filteredSeals = customSeals.value?.filter((item: any) => {
      return item.position === 'patronages';
    });

    return filteredSeals;
  });

  const customSealsOnLicenseText = computed<any>(() => {
    if (!customSeals.value) {
      return;
    }

    const filteredSeals = customSeals.value?.filter((item: any) => {
      return item.position === 'license';
    });

    return filteredSeals;
  });

  const customSealsOnBottomSeals = computed<any>(() => {
    if (!customSeals.value) {
      return;
    }

    const filteredSeals = customSeals.value?.filter((item: any) => {
      return item.position === 'seals';
    });

    return filteredSeals;
  });

  function hasSealType(type: SealType) {
    const filtered = customSeals.value?.filter((item: any) => {
      return item.type === type;
    });

    return !!filtered.length;
  }

  function idFromSealType(type: SealType) {
    const filtered = customSeals.value?.find((item: any) => {
      return item.type === type;
    });

    if (!filtered || !filtered?.id) {
      return;
    }

    return filtered.id;
  }

  return {
    primarySeal,
    primarySealId,
    primarySealIsAnjseal,
    primarySealIsAntillephone,
    primarySealIsTypeCuracao,
    primarySealIsTypeBase,
    primarySealIsTypeImage,
    licenseText,
    customSealsOnPatronages,
    customSealsOnLicenseText,
    customSealsOnBottomSeals,
    hasSealType,
    idFromSealType,
  };
};
